import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import TariffsRouteModal from './components/TariffsRouteModal'
import AuthRouteModal from './components/AuthRouteModal'
import { backgroundOf, isAuthModalPath } from './utils/routeModal'
import { RouteModalContext } from './context/RouteModalContext'
import { forgetLibraryOrigin, isLibraryPath } from './pages/assets/libraryBack'
import NavRouteGuard from './components/NavRouteGuard'
import ToolsIndexRedirect from './components/ToolsIndexRedirect'
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import GenerationStickers from './components/GenerationStickers'
import RefusalNoticeHost from './components/RefusalNoticeHost'
import MotionRiskConfirmHost from './components/MotionRiskConfirm'
import Login from './pages/Login'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import Tariffs from './pages/Tariffs'
import type { Model } from './data/models'
import { useProviders } from './hooks/useProviders'
import { useAuth } from './context/AuthContext'
import { GenerateProvider } from './context/GenerateContext'
import { VariantProvider } from './context/VariantContext'
import ToastContainer from './components/Toast'
import TopUpHost from './components/TopUpHost'
import ImageCropHost from './components/ImageCropHost'
import CharacterCreatorHost from './components/CharacterCreatorHost'
import SupportWidget from './components/SupportWidget'
import Loader from './components/ui/Loader'
import { isDiagOn } from './utils/diag'
import { storePromo } from './api/promo'
import GlobalAmbient from './components/GlobalAmbient'

// Страницы, которые не нужны на старте, грузятся при заходе в них (30.09): общий скрипт тянул их на КАЖДУЮ
// страницу — замер первого захода на тренды: 2,9 МБ кода, из них полный набор иконок главной 577 КБ (HomeTemp
// импортирует lucide-react целиком), формулы чата 255 КБ (katex), кабинет 126 КБ, оферта с политикой 72 КБ.
// Вход, регистрация и тарифы (оверлей тарифов нужен сразу) остаются в общем скрипте. Первый кадр прямого
// захода закрывает готовая страница (src/bootShell.ts), переход внутри сайта — Suspense вокруг Routes.
const Bots = lazy(() => import('./pages/bots/pages/Bots.tsx'))
const Chat = lazy(() => import('./pages/Chat'))
const Generate = lazy(() => import('./pages/generate/Generate.tsx'))
const Files = lazy(() => import('./pages/Files'))
const LoaderDemo = lazy(() => import('./pages/LoaderDemo'))
const Account = lazy(() => import('./pages/account/Account.tsx'))
// Панель скрытого режима проверки (?diag=1) — отдельный кусок, обычному посетителю не грузится.
const DiagPanel = lazy(() => import('./components/DiagPanel'))
const TariffsTest = lazy(() => import('./pages/TariffsTest'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const TelegramCallback = lazy(() => import('./pages/TelegramCallback'))
const TelegramLinkCallback = lazy(() => import('./pages/TelegramLinkCallback'))
const Verify = lazy(() => import('./pages/Verify'))
const VerifyPending = lazy(() => import('./pages/VerifyPending'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Offer = lazy(() => import('./pages/Offer'))
const HomeTemp = lazy(() => import('./pages/HomeTemp'))

// Code-split тяжёлых /bots/* страниц — их посещают реже, а они таскают
// за собой CardGallery со стилями, StylesContext и т.п. В main bundle
// не нужны.
const BotsImages = lazy(() => import('./pages/bots/pages/BotsImages.tsx'))
const BotsVideo = lazy(() => import('./pages/bots/pages/BotsVideo.tsx'))
const BotsAudio = lazy(() => import('./pages/bots/pages/BotsAudio.tsx'))
const BotsTrends = lazy(() => import('./pages/bots/pages/BotsTrends.tsx'))
const BotsSlides = lazy(() => import('./pages/bots/pages/BotsSlides.tsx'))
const BotsPipeline = lazy(() => import('./pages/bots/pages/BotsPipeline.tsx'))
const ProductCardLab = lazy(() => import('./pages/bots/pages/ProductCardLab.tsx'))
const FlowsPage = lazy(() => import('./pages/flows/FlowsPage.tsx'))
// «Сцена» — отдельная страница-студия по образцу Artlist Studio (владелец 30.09): вход, композер, монтаж.
const SceneHome = lazy(() => import('./pages/scene/SceneHome.tsx'))
const SceneProjectPage = lazy(() => import('./pages/scene/SceneProjectPage.tsx'))
const SceneEditPage = lazy(() => import('./pages/scene/SceneEditPage.tsx'))
const AssetSectionPage = lazy(() => import('./pages/assets/AssetSectionPage.tsx'))
const AssetPage = lazy(() => import('./pages/assets/AssetPage.tsx'))
const FlowCanvasPage = lazy(() => import('./pages/flows/FlowCanvasPage.tsx'))
const AssistantsPage = lazy(() => import('./pages/assistants/AssistantsPage.tsx'))
const SharedDocPage = lazy(() => import('./pages/assistants/SharedDocPage.tsx'))
const VerifyPage = lazy(() => import('./pages/assistants/VerifyPage.tsx'))
// Ссылка из письма на новую почту (смена почты с подтверждением, 27.09).
const ConfirmEmail = lazy(() => import('./pages/ConfirmEmail.tsx'))
const HoroscopePublicPage = lazy(() => import('./pages/assistants/HoroscopePublicPage.tsx'))
const TarotCardsPage = lazy(() => import('./pages/assistants/TarotCardsPage.tsx'))
const ReadingJournalPage = lazy(() => import('./pages/assistants/ReadingJournalPage.tsx'))
const BotsToolCovers = lazy(() => import('./pages/bots/pages/BotsToolCovers.tsx'))
const BotsAssistants = lazy(() => import('./pages/bots/pages/BotsAssistants.tsx'))
import BotsMediaLayout from './pages/bots/components/BotsMediaLayout.tsx'

// Презентации переехали из таба /bots/slides в инструмент /bots/tools/slides.
// Старый URL (индексирован, в закладках, во внешних ссылках) редиректим на
// новый с сохранением подпути (folder/:id), replace — чтобы не копить историю.
function SlidesLegacyRedirect() {
	const { '*': rest } = useParams()
	return <Navigate to={`/bots/tools/slides${rest ? `/${rest}` : ''}`} replace />
}

// Вкладка изображений переехала со /styles на /photostudio (2026-08-03, вслед
// за названием «Фотостудия»). Подменяем ПРЕФИКС пути, а не собираем адрес из
// параметров: форм у старого адреса четыре (корень, категория, лендинг
// карточки, категория + лендинг), и у части из них хвост попадает не в `*`, а
// в именованные параметры соседнего роута.
function PhotostudioPathRedirect() {
	const { pathname, search } = useLocation()
	return (
		<Navigate to={pathname.replace('/bots/images/styles', '/bots/images/photostudio') + search} replace />
	)
}

export default function App() {
	// Роут-модал тарифов: переход на /tariffs изнутри приложения кладёт
	// текущую локацию в state.backgroundLocation (см. useOpenTariffs). Тогда
	// <Routes> рендерит эту подложку, а модал ложится оверлеем поверх —
	// возврат мгновенный, подложка не размонтируется. Прямой заход/refresh
	// на /tariffs состояния не имеет → рендерим полную страницу (SEO цел).
	const location = useLocation()
	const navigate = useNavigate()

	// Прямой заход/F5 на prerender-страницу приходит с трейлинг-слэшем
	// (nginx: /generate → 301 /generate/ — сервинг каталога снапшотов).
	// Точные сравнения pathname по коду от этого ломаются (Generate рендерил
	// null-страницу, Header терял режим). Нормализуем URL сразу после
	// гидрации: client-side replace без перезагрузки, query/hash/state целы.
	useLayoutEffect(() => {
		if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
			navigate(
				{
					pathname: location.pathname.replace(/\/+$/, ''),
					search: location.search,
					hash: location.hash,
				},
				{ replace: true, state: location.state },
			)
		}
	}, [location, navigate])

	const tariffsBackground = location.pathname === '/tariffs' ? backgroundOf(location) : null
	// Окно входа поверх страницы — тот же приём (useOpenAuth): страница, с которой гость нажал «Регистрация»
	// или «Войти», остаётся под окном. Прямой заход/F5 на /login, /register — полная страница, как раньше.
	const authBackground = isAuthModalPath(location.pathname) ? backgroundOf(location) : null

	// «Вошли в библиотеку из кабинета» помнится, пока человек в библиотеке (аватары, локации, предметы); ушёл — «‹»
	// библиотеки снова ведёт к «Сцене». Под окном тарифов или входа страница — его подложка, метку не трогаем.
	const pagePath = (tariffsBackground ?? authBackground ?? location).pathname
	useEffect(() => {
		if (!isLibraryPath(pagePath)) forgetLibraryOrigin()
	}, [pagePath])

	useEffect(() => {
		localStorage.removeItem('utm_campaign')

		const params = new URLSearchParams(window.location.search)
		const ref = params.get('ref')
		if (ref) localStorage.setItem('referral_code', ref)
		// ?promo=CODE — промокод из рассылки/поста: подставится в поля оплаты и
		// активации в кабинете (storePromo сам ловит ошибку localStorage)
		const promo = params.get('promo')
		if (promo) storePromo(promo)

		const stored = localStorage.getItem('umnik_utm')
		if (stored) {
			try {
				const parsed = JSON.parse(stored)
				if (parsed.captured_at && Date.now() - parsed.captured_at < 90 * 24 * 3600 * 1000) {
					return
				}
			} catch { /* невалидный JSON — перезаписать */ }
		}

		const utmSource = params.get('utm_source')
		const utmMedium = params.get('utm_medium')
		const utmCampaign = params.get('utm_campaign')
		const utmTerm = params.get('utm_term')
		const utmContent = params.get('utm_content')

		localStorage.setItem('umnik_utm', JSON.stringify({
			utm_source: utmSource || '',
			utm_medium: utmMedium || '',
			utm_campaign: utmCampaign || '',
			utm_term: utmTerm || '',
			utm_content: utmContent || '',
			referrer: document.referrer || '',
			landing_page: window.location.pathname || '',
			captured_at: Date.now(),
		}))
	}, [])

	const [selectedModel, setSelectedModel] = useState<Model | null>(() => {
		try {
			const saved = localStorage.getItem('selectedModel')
			if (saved) {
				const model = JSON.parse(saved) as Model
				// Не-текстовые модели раньше выбрасывались ВСЕГДА — это лечило
				// заход на /chat с картиночной моделью в хранилище, но заодно
				// ломало F5 на /generate: модель обнулялась, и дефолтный эффект
				// страницы сажал первую t2i из каталога (юзер выбрал Face Swap —
				// после перезагрузки видел Nano Banana). Восстанавливаем их там,
				// где они и нужны: на страницах генерации. Для /chat поведение
				// прежнее (null → чат сам выберет текстовую).
				const path = typeof window !== 'undefined' ? window.location.pathname : ''
				const mediaPage = path.startsWith('/generate') || path.startsWith('/bots')
				if (model.category !== 'text' && !mediaPage) return null
				return model
			}
		} catch {
			/* ignore */
		}
		return null
	})

	const handleSelectModel = useCallback((model: Model | null) => {
		setSelectedModel(model)
		if (model) {
			localStorage.setItem('selectedModel', JSON.stringify(model))
		} else {
			localStorage.removeItem('selectedModel')
		}
	}, [])

	// Модель в localStorage лежит целиком, вместе с ценой на момент выбора.
	// После смены цены в админке юзер видел старую цену (бейдж стоимости в
	// чате, блок памяти контекста) до ручного перевыбора модели. Когда
	// приезжает свежий список из /providers — подменяем сохранённую копию
	// актуальной, если ценовые поля разошлись.
	const { allModels } = useProviders()
	useEffect(() => {
		if (!selectedModel || allModels.length === 0) return
		const fresh = allModels.find(
			(m) => m.id === selectedModel.id && m.provider === selectedModel.provider,
		)
		if (!fresh) return
		if (
			fresh.cost !== selectedModel.cost ||
			fresh.defaultCost !== selectedModel.defaultCost ||
			JSON.stringify(fresh.variants ?? null) !== JSON.stringify(selectedModel.variants ?? null)
		) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- синк выбранной модели с ценами, приехавшими с бэка: внешние данные, не производное состояние
			setSelectedModel(fresh)
			localStorage.setItem('selectedModel', JSON.stringify(fresh))
		}
	}, [allModels, selectedModel])

	const [sidebarOpen, setSidebarOpen] = useState(false)
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
	// Сжатие сайдбара транслируем на body: --sidebar-width становится живой
	// (см. _variables.scss), и fixed-слои — компакт-виджет .gw на /bots —
	// следуют за панелью так же, как контент через margin. Класс именно на
	// body: у Sidebar и main общего элемента-предка нет (фрагмент).
	useEffect(() => {
		document.body.classList.toggle('is-sidebar-collapsed', sidebarCollapsed)
		return () => document.body.classList.remove('is-sidebar-collapsed')
	}, [sidebarCollapsed])
	const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0)
	const [iconVersion, setIconVersion] = useState(0)
	const { loading } = useAuth()

	const handleNewSession = useCallback((_sessionId: string, _model: string | null) => {
		setSidebarRefreshKey((k) => k + 1)
	}, [])
	// Умные помощники (10.09): разговор вынесли в меню, переименовали или убрали — список чатов перечитывается по событию.
	useEffect(() => {
		const onChanged = () => setSidebarRefreshKey((k) => k + 1)
		window.addEventListener('umnik:chats-changed', onChanged)
		return () => window.removeEventListener('umnik:chats-changed', onChanged)
	}, [])

	const handleSessionUpdated = useCallback((_sessionId: string, _model: string | null) => {
		setIconVersion((v) => v + 1)
	}, [])

	if (loading) {
		// Проверка входа: пустой экран — тоже ожидание, стартовый слой с готовой страницей его перекрывает.
		return <div className="app-loading" data-boot-wait="" />
	}

	return (
		// Открыто окно тарифов или входа — подсказки-туры страниц ждут его закрытия (useRouteModalOpen)
		<RouteModalContext.Provider value={!!(tariffsBackground || authBackground)}>
			{/* Global ambient (atmosphere + orbs) — единый слой для всего сайта
			    кроме auth-страниц. Раньше жил в HomeTemp.tsx (только /), теперь
			    DRY на App-level. Brand-reactive через selectedModel — orbs
			    подкрашиваются в цвет выбранной модели (см. GlobalAmbient.tsx). */}
			<GlobalAmbient selectedModel={selectedModel} />

			<Sidebar
				isOpen={sidebarOpen}
				onClose={() => setSidebarOpen(false)}
				refreshKey={sidebarRefreshKey}
				onSelectModel={handleSelectModel}
				collapsed={sidebarCollapsed}
				onToggleCollapsed={setSidebarCollapsed}
				iconVersion={iconVersion}
				/>

			{/* <main> вместо div: семантика для SEO/скринридеров (блок 2.6
			    протокола). Класс и каскад не меняются — селекторы классовые. */}
			<main className={`main-content ${sidebarCollapsed ? 'is-sidebar-collapsed' : ''}`}>
				<Header
					onMenuClick={() => setSidebarOpen(true)}
					selectedModel={selectedModel}
					onSelectModel={handleSelectModel}
					isSidebarOpen={sidebarOpen}
				/>

				{/* Стикеры незакрытых генераций. Внутри <main>, а не в шапке:
				    позиционируются fixed и не должны раздвигать её содержимое.
				    Выше Routes — переживают смену страницы. */}
				<GenerationStickers />
				{/* Окна «Отказов нейросетей»: предупреждение за нарушение и «токены списаны» — в любом разделе. */}
				<RefusalNoticeHost />
				<MotionRiskConfirmHost />

				{/*
				  GenerateProvider поднят с уровня /generate-страницы на App-level.
				  Это даёт shared state между home и /generate: юзер настраивает
				  модель + промпт + settings на главной → переходит на /generate
				  → один и тот же экземпляр Provider, generation стартует без
				  marshalling через location.state.

				  /bots/* widgets имеют СВОЙ внутренний GenerateProvider в
				  GenerateWidget (изолированный state для widget-режима) — он
				  перекрывает этот App-level Provider в своём поддереве через
				  обычный context-resolution.

				  Provider tolerate `model === null` (см. GenerateContext: useState
				  init + useEffect на model?.id). Home рендерится до выбора модели
				  — Provider просто отдаёт DEFAULT_SETTINGS из localStorage.
				*/}
				<VariantProvider>
				<GenerateProvider model={selectedModel} onModelChange={handleSelectModel}>
				{/* Страж разделов: если пункт скрыт в админке («Настройки → Меню»),
				    прямая ссылка уводит на /bots. Один на приложение — подпути
				    разделов закрываются вместе с ними (владелец, 10.09). */}
				<NavRouteGuard />
				{/* Общий Suspense ленивых страниц без своего (главная, чат, кабинет, оферта…). */}
				<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
				<Routes location={tariffsBackground ?? authBackground ?? location}>
					<Route
						path="/"
						element={
							<HomeTemp
								selectedModel={selectedModel}
								onSelectModel={handleSelectModel}
								pageSlug="home"
							/>
						}
					/>
					<Route path="/bots" element={<Bots onSelectModel={handleSelectModel} />} />
					<Route
						path="/chat"
						element={
							<Chat
								selectedModel={selectedModel}
								onNewSession={handleNewSession}
								onSelectModel={handleSelectModel}
								onSessionUpdated={handleSessionUpdated}
							/>
						}
					/>
					<Route
						path="/chat/:sessionId"
						element={
							<Chat
								selectedModel={selectedModel}
								onNewSession={handleNewSession}
								onSelectModel={handleSelectModel}
								onSessionUpdated={handleSessionUpdated}
							/>
						}
					/>
					<Route
						path="/generate"
						element={<Generate selectedModel={selectedModel} onSelectModel={handleSelectModel} />}
					/>
					<Route path="/files" element={<Files />} />
					{/* «Флоу» — узловой холст генераций: список и холст живут в общей
					    рамке приложения (без панели /bots и виджета), раздел staffOnly. */}
					<Route path="/flows" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<FlowsPage />
						</Suspense>
					} />
					{/* Превью шаблона: холст открывается без записи в базу, свой флоу появляется
					    при первом действии (правка, фото, запуск) — ленивая копия. */}
					<Route path="/flows/t/:templateId" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<FlowCanvasPage />
						</Suspense>
					} />
					<Route path="/flows/:flowId" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<FlowCanvasPage />
						</Suspense>
					} />
					{/* «Сцена»: входная, проект (композер) и монтаж; /scene/p/example — проект-пример только для чтения. */}
					<Route path="/scene" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<SceneHome />
						</Suspense>
					} />
					<Route path="/scene/example" element={<Navigate to="/scene/p/example" replace />} />
					{/* Шаблон по id — та же страница проекта только для чтения (пример «на входной» — /scene/p/example). */}
					<Route path="/scene/t/:templateId" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<SceneProjectPage />
						</Suspense>
					} />
					<Route path="/scene/p/:projectId" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<SceneProjectPage />
						</Suspense>
					} />
					<Route path="/scene/p/:projectId/edit" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<SceneEditPage />
						</Suspense>
					} />
					{/* Персонажи, локации, предметы — как у Artlist (02.10): витрина раздела и страница ассета с версиями. */}
					{(['character', 'location', 'prop'] as const).map((k) => {
						const base = k === 'character' ? '/characters' : k === 'location' ? '/locations' : '/props'
						const page = <Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}><AssetPage kind={k} /></Suspense>
						return [
							<Route key={`${k}-list`} path={base} element={<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}><AssetSectionPage kind={k} /></Suspense>} />,
							<Route key={`${k}-page`} path={`${base}/:id`} element={page} />,
							<Route key={`${k}-version`} path={`${base}/:id/history/:snapshotId`} element={page} />,
						]
					})}
					{/* «Умные помощники» — новый раздел параллельно /bots/assistants (владелец 06.09):
					    рельса помощников, история, диалог, документ. staffOnly + флаг assistants.enabled на бэке. */}
					<Route path="/assistants" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<AssistantsPage />
						</Suspense>
					} />
					{/* Документ умного помощника по ссылке (совместный доступ): без входа — просмотр и комментарии. */}
					<Route path="/d/:token" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<SharedDocPage />
						</Suspense>
					} />
					{/* Проверка подписи документа по ключу из штампа (публично). */}
					<Route path="/assistants/verify/:key?" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<VerifyPage />
						</Suspense>
					} />
					{/* Энциклопедия карт Таро (15.09): публично, для поиска; расклад — у помощника. */}
					<Route path="/assistants/tarot/cards/:id?" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<TarotCardsPage />
						</Suspense>
					} />
					{/* Дневник чтений (15.09): свои расклады и что сбылось — только для вошедших. Слово journal отсечено в RESERVED_SLUGS. */}
					<Route path="/assistants/journal" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<ReadingJournalPage />
						</Suspense>
					} />
					{/* Гороскоп для поиска (11.09): один знак на сегодня публично, остальное за входом. */}
					<Route path="/goroskop/:slug?" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<HoroscopePublicPage />
						</Suspense>
					} />
					{/* Посадочная помощника: /assistants/astrologer — сюда ведут статьи и реклама.
					    ⚠️ Объявлять ПОСЛЕ /assistants/verify/:key: слуг не должен перехватывать проверку
					    подписи документа. В коде слово verify тоже отсечено (RESERVED_SLUGS). */}
					<Route path="/assistants/:slug" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<AssistantsPage />
						</Suspense>
					} />
					<Route path="/loader-demo" element={<LoaderDemo />} />
					<Route path="/login" element={<Login />} />
					<Route path="/register" element={<Register />} />
					<Route path="/forgot-password" element={<ForgotPassword />} />
					<Route path="/account" element={<Account />} />
					<Route path="/tariffs" element={<Tariffs />} />
					<Route path="/tariffs_test" element={<TariffsTest />} />
					<Route path="/payment/success" element={<PaymentSuccess />} />
					<Route path="/payment/fail" element={<PaymentSuccess failed />} />
					<Route path="/auth/telegram-callback" element={<TelegramCallback />} />
					<Route path="/auth/telegram-link-callback" element={<TelegramLinkCallback />} />
					<Route path="/verify" element={<Verify />} />
					<Route path="/verify-pending" element={<VerifyPending />} />
					<Route path="/confirm-email" element={
						<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
							<ConfirmEmail />
						</Suspense>
					} />
					<Route path="/privacy" element={<Privacy />} />
					<Route path="/offer" element={<Offer />} />
					<Route
						path="/main_temp"
						element={
							<HomeTemp
								selectedModel={selectedModel}
								onSelectModel={handleSelectModel}
								pageSlug="main_temp"
							/>
						}
					/>
					<Route element={<BotsMediaLayout onSelectModel={handleSelectModel} />}>
						<Route path="/bots/images/tool/:cardSlug" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsImages />
							</Suspense>
						} />
						<Route path="/bots/video/tool/:cardSlug" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsVideo />
							</Suspense>
						} />
						<Route path="/bots/trends/tool/:cardSlug" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsTrends />
							</Suspense>
						} />
						{/* Лендинг карточки С СОХРАНЕНИЕМ контекста (категория/all/popular):
						    пебл и сетка под виджетом остаются те, из которых пришли —
						    юзер не вылетает в «Все» (жалоба владельца 2026-07-24).
						    Canonical у лендинга всегда корневой /tool/… — дублей нет. */}
						<Route path="/bots/trends/:galleryCtx/tool/:cardSlug" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsTrends />
							</Suspense>
						} />
						{/* «Фотостудия» — вкладка-лейн внутри изображений, поэтому её
						    контекст витрины двухсегментный: /photostudio/<категория>.
						    Роут стоит ВЫШЕ одиночного :galleryCtx — иначе тот перехватил
						    бы путь и категория потерялась бы. Лендинг без категории
						    (/bots/images/photostudio/tool/…) матчится общим роутом ниже. */}
						<Route path="/bots/images/photostudio/:galleryCtx/tool/:cardSlug" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsImages />
							</Suspense>
						} />
						{/* Старый адрес вкладки (до переименования в «Фотостудию»).
						    Держим ради живых ссылок и закладок: снаружи их ловит 301 в
						    nginx, здесь — переходы внутри SPA, куда nginx не достаёт.
						    Лендинг карточки объявлен ОТДЕЛЬНО и выше: путь вида
						    /bots/images/styles/tool/<slug> иначе перехватывает роут
						    :galleryCtx/tool/:cardSlug (у него ctx = «styles»), и вместо
						    редиректа юзер попадал на корень раздела. */}
						<Route path="/bots/images/styles/tool/:cardSlug" element={<PhotostudioPathRedirect />} />
						<Route path="/bots/images/styles/*" element={<PhotostudioPathRedirect />} />
						<Route path="/bots/images/:galleryCtx/tool/:cardSlug" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsImages />
							</Suspense>
						} />
						<Route path="/bots/video/:galleryCtx/tool/:cardSlug" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsVideo />
							</Suspense>
						} />
						<Route path="/bots/images/*" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsImages />
							</Suspense>
						} />
						<Route path="/bots/video/*" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsVideo />
							</Suspense>
						} />
						<Route path="/bots/audio/*" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsAudio />
							</Suspense>
						} />
						<Route path="/bots/trends/*" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsTrends />
							</Suspense>
						} />
						<Route path="/bots/slides/*" element={<SlidesLegacyRedirect />} />
						{/* Каталог «Лабораторий» упразднён (владелец 2026-08-14):
						    навигация — раскрывающийся пункт сайдбара; старый URL
						    уводим на первую вкладку. */}
						<Route path="/bots/tools" element={<ToolsIndexRedirect />} />
						<Route path="/bots/tools/product_card" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<ProductCardLab />
							</Suspense>
						} />
						<Route path="/bots/tools/product-card" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<ProductCardLab />
							</Suspense>
						} />
						<Route path="/bots/tools/covers" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsToolCovers />
							</Suspense>
						} />
						<Route path="/bots/tools/:pipelineType" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsPipeline />
							</Suspense>
						} />
					</Route>
					{/* Презентации — студия прямо на фоне страницы, без стеклянной
					    панели раздела и её сетки (решение владельца 02.09: рабочая
					    область как на /generate, а не «контейнер в контейнере»). Тот же
					    layout с виджетом, но в голой рамке. */}
					<Route element={<BotsMediaLayout frame="bare" onSelectModel={handleSelectModel} />}>
						<Route path="/bots/tools/slides/*" element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsSlides />
							</Suspense>
						} />
					</Route>
					<Route
						path="/bots/assistants"
						element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsAssistants
									onSelectModel={handleSelectModel}
									onNewSession={handleNewSession}
								/>
							</Suspense>
						}
					/>
					<Route
						path="/bots/assistants/:agentSlug"
						element={
							<Suspense fallback={<Loader size="lg" centered label="Загружаем..." />}>
								<BotsAssistants
									onSelectModel={handleSelectModel}
									onNewSession={handleNewSession}
								/>
							</Suspense>
						}
					/>
				</Routes>
				</Suspense>

				{/* Тарифы-оверлей поверх подложки (Tariffs внутри — тот же
				    компонент, что и на полной странице /tariffs) */}
				{tariffsBackground && <TariffsRouteModal />}
				</GenerateProvider>
				</VariantProvider>
			</main>

			{/* Вход и регистрация окном поверх страницы (Login/Register внутри — те же, что на полных страницах) */}
			{authBackground && <AuthRouteModal />}

			<ToastContainer />
			<ImageCropHost />
			<TopUpHost />
			<CharacterCreatorHost />
			<SupportWidget />
			{isDiagOn() && (
				<Suspense fallback={null}>
					<DiagPanel />
				</Suspense>
			)}

		</RouteModalContext.Provider>
	)
}
